<template>
  <div class="min-h-screen bg-black">
    <div class="w-full flex justify-center">
      <div class="w-full max-w-[900px] py-8 px-4 sm:px-8">
        <NuxtLink to="/" class="inline-flex items-center text-turquoise-500 hover:text-turquoise-400 mb-8 group">
          <Icon name="mdi:arrow-left" class="mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </NuxtLink>

        <h1 class="text-3xl font-light text-white mb-8">Oxbull Frequently Asked Questions</h1>

        <div class="prose prose-invert">
          <div class="space-y-8">
            <div>
              <h2>What is Oxbull?</h2>
              <p>Oxbull is a crypto launchpad that helps new blockchain projects raise funds through Initial DEX Offerings (IDOs). Launched in 2021, it supports projects across multiple chains and has been recognized as a top-performing launchpad, with an all-time high ROI of 160.14x.</p>
              <p>OXI is the official token of the Oxbull ecosystem.</p>
            </div>

            <div>
              <h2>How does a crypto launchpad like Oxbull work?</h2>
              <p>Oxbull works by connecting vetted crypto projects with early investors. Users stake the OXI token to gain allocation tiers, which determine their access to investment rounds. After staking, users can participate in IDOs and claim tokens post-launch.</p>
            </div>

            <div>
              <h2>What blockchain is Oxbull on?</h2>
              <p>Oxbull is currently active on the Base Layer 2 blockchain.</p>
              <p>In December 2024, Oxbull officially migrated its ecosystem to the Base Layer 2 blockchain. As part of this transition, the legacy tokens OXB (BNB Chain) is now OXI, deployed on Base.</p>
            </div>

            <div>
              <h2>What is the OXI token?</h2>
              <p>OXI is the utility and governance token of the Oxbull ecosystem. It is used for:</p>
              <ul>
                <li>Staking</li>
                <li>Participating in IDOs</li>
                <li>Voting</li>
                <li>Community and ecosystem access</li>
              </ul>
              <p>OXI runs on Base and replaced the earlier OXB (BSC) and OXS (Solana) tokens after the December 2024 migration.</p>
            </div>

            <div>
              <h2>What is the OXI tokenomics?</h2>
              <p>OXI has a fixed total supply of 12,345,678 tokens. Here's the distribution:</p>
              <ul class="list-disc pl-6">
                <li>Circulating Supply (owned by Oxbull and Kwai holders): 92.75%</li>
                <li>Uniswap Liquidity: 4.25%</li>
                <li>Team Token: 3%</li>
              </ul>
            </div>

            <div>
              <h2>Where can I buy OXI?</h2>
              <p>You can buy OXI on <a href="https://app.uniswap.org/swap?outputCurrency=0x07fb80f7A8460DE3065d74B2663ba0D740fB3cA5&chain=base" class="text-turquoise-500 hover:text-turquoise-400" rel="noopener" target="_blank">Uniswap (Base)</a>. Always verify the correct contract address before purchasing:</p>
              <strong>OXI Token (Base)</strong>
              <code class="bg-white/5 p-2 rounded block">0x07fb80f7A8460DE3065d74B2663ba0D740fB3cA5</code>
            </div>

            <div>
              <h2>How do I join an IDO on Oxbull?</h2>
              <ol class="list-decimal pl-6">
                <li>Buy and <a href="https://oxbull.tech/app/stake" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">stake</a> OXI tokens</li>
                <li>Qualify for a tier based on your staked amount</li>
                <li>Check the <a href="https://oxbull.tech/app/calendar" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">launch calendar</a> for upcoming projects</li>
                <li>Join the IDO during your tier's allocation window</li>
                <li>Claim your tokens after the TGE (Token Generation Event)</li>
              </ol>
            </div>

            <div>
              <h2>What is the Oxbull tier system?</h2>
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead>
                    <tr class="border-b border-white/10">
                      <th class="text-left py-2">Tier</th>
                      <th class="text-left py-2">OXI required</th>
                      <th class="text-left py-2">Allocation model</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="border-b border-white/10">
                      <td class="py-2"><strong>Tier 1</strong></td>
                      <td>8,000 OXI</td>
                      <td>Highest guaranteed allocation</td>
                    </tr>
                    <tr class="border-b border-white/10">
                      <td class="py-2"><strong>Tier 2</strong></td>
                      <td>1,000 – 4,000 OXI</td>
                      <td>Medium guaranteed allocation</td>
                    </tr>
                    <tr>
                      <td class="py-2"><strong>Tier 3</strong></td>
                      <td>50 OXI (min)</td>
                      <td>FCFS via whitelist</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2>What is the minimum to join Oxbull?</h2>
              <p>You can join Oxbull with as little as 50 OXI, qualifying for Tier 3 participation. Higher tiers offer guaranteed allocations.</p>
            </div>

            <div>
              <h2>How do I stake OXI?</h2>
              <ol class="list-decimal pl-6">
                <li>Visit <a href="https://oxbull.tech/app/stake" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">oxbull.tech/app/stake</a></li>
                <li>Connect your Base wallet</li>
                <li>Stake any amount</li>
                <li>Approve the transaction</li>
                <li>Staking rewards begin immediately</li>
              </ol>
            </div>

            <div>
              <h2>What are the benefits of staking OXI?</h2>
              <ul class="list-disc pl-6">
                <li>Guaranteed allocation in IDOs (depending on tier)</li>
                <li>Access to exclusive private groups (e.g., Telegram)</li>
                <li>Daily compounded rewards (2.5% APR)</li>
              </ul>
            </div>

            <div>
              <h2>Where can I see upcoming launches?</h2>
              <p>Visit <a href="https://oxbull.tech/app/calendar" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">oxbull.tech/app/calendar</a> to view upcoming token launches. Each listing includes tier details, contribution timelines, and project info.</p>
            </div>

            <div>
              <h2>Can I get a refund after joining an IDO?</h2>
              <p>Yes. Oxbull offers a 24-hour refund window after a project's Token Generation Event (TGE), known as <a href="https://docs.oxbull.tech/oxbull-immunity-refund-policy" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">Oxbull Immunity</a>. Projects that opt into this feature allow participants to request a full refund during that window.</p>
            </div>

            <div>
              <h2>What is Oxbull Immunity?</h2>
              <p><a href="https://docs.oxbull.tech/oxbull-immunity-refund-policy" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">Oxbull Immunity</a> is a refund policy that allows users to withdraw their contribution within 24 hours after the token launch (TGE) for eligible projects. It's a unique user-first policy designed to protect your capital.</p>
            </div>

            <div>
              <h2>What happened to OXB and OXS?</h2>
              <p>OXB (on BNB Chain) and OXS (on Solana) were Oxbull's earlier tokens. In December 2024, Oxbull migrated its ecosystem to the Base Layer 2 network, and adopted the new token ticker OXI.</p>
              <p>OXI is now the <strong>official token</strong> of the Oxbull ecosystem.</p>
            </div>

            <div>
              <h2>Why did Oxbull move to Base?</h2>
              <p>The move to Base was driven by a long-term vision to build on a fast, developer-friendly, and scalable L2 backed by Coinbase. Base offers:</p>
              <ul class="list-disc pl-6">
                <li>Lower gas fees</li>
                <li>A vibrant L2 ecosystem</li>
                <li>Easy onboarding for new users</li>
              </ul>
              <p>Oxbull's migration is part of its evolution toward supporting next-generation projects more efficiently, with a unified experience for both users and developers.</p>
            </div>

            <div>
              <h2>Is OXB still used?</h2>
              <p>No. OXB is no longer in active use. All utilities, staking, and token launch access have been migrated to OXI on Base. Holding OXB does not grant any access to the current Oxbull ecosystem.</p>
              <p>We strongly recommend all OXB holders complete the migration to OXI as soon as possible.</p>
            </div>

            <div>
              <h2>How can I migrate from OXB to OXI?</h2>
              <p><strong>For OXB v1 Holders who have yet to upgrade your tokens:</strong></p>
              <ol class="list-decimal pl-6">
                <li>Visit <a href="https://oxbull.tech/app/account" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">oxbull.tech/app/account</a></li>
                <li>Connect your wallet and link Telegram</li>
                <li>You should see a prompt to upgrade your OXB v1 tokens</li>
                <li>Click "Upgrade" and approve the necessary transactions via your wallet</li>
                <li>Your OXB tokens should now be successfully upgraded to OXI, and autostaked in <a href="https://oxbull.tech/app/stake" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">Oxbull staking page</a></li>
              </ol>
            </div>

            <div>
              <h2>How can I migrate from OXS to OXI?</h2>
              <p><strong>For OXS Holders who have yet to migrate your tokens:</strong></p>
              <ol class="list-decimal pl-6">
                <li>Visit <a href="https://v2.oxbull.tech/app/oxs-migrate" class="text-turquoise-500 hover:text-turquoise-400" target="_blank" rel="noopener">v2.oxbull.tech/app/oxs-migrate</a></li>
                <li>Connect your Solana wallet to the migration page</li>
                <li>Provide your Base wallet address by connecting your wallet to the site</li>
                <li>Click "Migrate" and approve the necessary transactions via your wallet</li>
                <li>Your OXS tokens should now be successfully migrated to OXI in your Base wallet</li>
              </ol>
            </div>
          </div>

          <p class="text-sm text-white/60 mt-12">Last updated 24 Apr 2025</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
definePageMeta({
  name: "faq",
});

import { usePosthogStore } from "~/stores/posthogStore";
const posthogStore = usePosthogStore();

onMounted(() => {
  // Initialize PostHog
  posthogStore.initPosthog();
});

useHead({
  title: 'Oxbull | FAQ',
  meta: [
    { name: 'title', content: "Oxbull | FAQ" },
    { name: 'description', content: "Find answers to frequently asked questions about Oxbull, OXI token, staking, tiers, and participation in IDOs/IGOs/INOs. Learn about how to migrate from OXB and OXS to OXI on Base." }
  ],
  htmlAttrs: { lang: "en" },
})


useJsonld([{
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
  {
      '@type': 'Question',
      name: 'What is Oxbull?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oxbull is a crypto launchpad that helps new blockchain projects raise funds through Initial DEX Offerings (IDOs). Launched in 2021, it supports projects across multiple chains and has been recognized as a top-performing launchpad, with an all-time high ROI of 160.14x. OXI is the official token of the Oxbull ecosystem.'
      }
    },
    {
      '@type': 'Question',
      name: 'How does a crypto launchpad like Oxbull work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oxbull works by connecting vetted crypto projects with early investors. Users stake the OXI token to gain allocation tiers, which determine their access to investment rounds. After staking, users can participate in IDOs and claim tokens post-launch.'
      }
    },
    {
      '@type': 'Question', 
      name: 'What blockchain is Oxbull on?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oxbull is currently active on the Base Layer 2 blockchain. In December 2024, Oxbull officially migrated its ecosystem to the Base Layer 2 blockchain. As part of this transition, the legacy tokens OXB (BNB Chain) is now OXI, deployed on Base.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the OXI token?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'OXI is the utility and governance token of the Oxbull ecosystem. It is used for: staking, participating in IDOs, voting, and community and ecosystem access. OXI runs on Base and replaced the earlier OXB (BSC) and OXS (Solana) tokens after the December 2024 migration.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the OXI tokenomics?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'OXI has a fixed total supply of 12,345,678 tokens. The distribution is: Circulating Supply (owned by Oxbull and Kwai holders): 92.75%, Uniswap Liquidity: 4.25%, Team Token: 3%.'
      }
    },
    {
      '@type': 'Question',
      name: 'Where can I buy OXI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can buy OXI on Uniswap (Base). Always verify the correct contract address: 0x07fb80f7A8460DE3065d74B2663ba0D740fB3cA5.'
      }
    },
    {
      '@type': 'Question',
      name: 'How do I join an IDO on Oxbull?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'To join an IDO: 1. Buy and stake OXI tokens, 2. Qualify for a tier based on your staked amount, 3. Check the launch calendar for upcoming projects, 4. Join the IDO during your tier\'s allocation window, 5. Claim your tokens after the TGE (Token Generation Event).'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the Oxbull tier system?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oxbull has three tiers: Tier 1 (8,000 OXI) offers highest guaranteed allocation, Tier 2 (1,000-4,000 OXI) provides medium guaranteed allocation, and Tier 3 (50 OXI minimum) offers FCFS via whitelist.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the minimum to join Oxbull?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can join Oxbull with as little as 50 OXI, qualifying for Tier 3 participation. Higher tiers offer guaranteed allocations.'
      }
    },
    {
      '@type': 'Question',
      name: 'How do I stake OXI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'To stake OXI: 1. Visit oxbull.tech/app/stake, 2. Connect your Base wallet, 3. Stake any amount, 4. Approve the transaction, 5. Staking rewards begin immediately.'
      }
    },
    {
      '@type': 'Question',
      name: 'What are the benefits of staking OXI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Benefits include: guaranteed allocation in IDOs (depending on tier), access to exclusive private groups (e.g., Telegram), daily compounded rewards (2.5% APR).'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I get a refund after joining an IDO?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Oxbull offers a 24-hour refund window after a project\'s Token Generation Event (TGE), known as Oxbull Immunity. Projects that opt into this feature allow participants to request a full refund during that window.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is Oxbull Immunity?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oxbull Immunity is a refund policy that allows users to withdraw their contribution within 24 hours after the token launch (TGE) for eligible projects. It\'s a unique user-first policy designed to protect your capital.'
      }
    },
    {
      '@type': 'Question',
      name: 'What happened to OXB and OXS?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'OXB (on BNB Chain) and OXS (on Solana) were Oxbull\'s earlier tokens. In December 2024, Oxbull migrated its ecosystem to the Base Layer 2 network, and adopted the new token ticker OXI. OXI is now the official token of the Oxbull ecosystem.'
      }
    },
    {
      '@type': 'Question',
      name: 'Why did Oxbull move to Base?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The move to Base was driven by a long-term vision to build on a fast, developer-friendly, and scalable L2 backed by Coinbase. Base offers: lower gas fees, a vibrant L2 ecosystem, and easy onboarding for new users. Oxbull\'s migration is part of its evolution toward supporting next-generation projects more efficiently, with a unified experience for both users and developers.'
      }
    },
    {
      '@type': 'Question',
      name: 'Is OXB still used?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. OXB is no longer in active use. All utilities, staking, and token launch access have been migrated to OXI on Base. Holding OXB does not grant any access to the current Oxbull ecosystem. We strongly recommend all OXB holders complete the migration to OXI as soon as possible.'
      }
    },
    {
      '@type': 'Question',
      name: 'How can I migrate from OXB to OXI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For OXB v1 Holders: 1. Visit oxbull.tech/app/account, 2. Connect your wallet and link Telegram, 3. Look for the prompt to upgrade OXB v1 tokens, 4. Click \'Upgrade\' and approve transactions, 5. Your OXB tokens will be upgraded to OXI and autostaked in the Oxbull staking page.'
      }
    },
    {
      '@type': 'Question',
      name: 'How can I migrate from OXS to OXI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For OXS Holders: 1. Visit v2.oxbull.tech/app/oxs-migrate, 2. Connect your Solana wallet, 3. Provide your Base wallet address, 4. Click \'Migrate\' and approve transactions, 5. Your OXS tokens will be migrated to OXI in your Base wallet.'
      }
    }
  ]
}]);


</script>

<style>
.prose {
  max-width: 900px;
}

.prose a {
  text-decoration: none;
}

.prose h2 {
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.prose h2 {
  color: white;
  margin-top: 1rem;
  margin-bottom: 0.75rem;
  font-size: 1.25rem;
}

.prose table {
  font-size: 0.875rem;
}

.prose code {
  color: #10b981;
}
</style>
